"use client";

import { DrawerCloseIcon } from "hec/assets/icons";
import type * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "~/lib/ui";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({ ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    ref={props.ref}
    className={cn(
      // `data-[state=closed]:!pointer-events-none` — see the matching note in
      // `hec/components/ui/drawer.tsx`. A closed-but-still-mounted full-screen
      // overlay is invisible and swallows every click, which shoppers report as
      // the page being "stuck". Caught in a Jam recording as a click landing on
      // this exact element with `data-state="closed" aria-hidden="true"`.
      // The `!` is load-bearing: Radix renders DialogPrimitive.Overlay with an
      // unconditional inline `style="pointer-events: auto"` while mounted, so a
      // non-important class never wins — including in the stall this guards
      // against.
      "fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=closed]:!pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  className,
  children,
  closeIconStyle,
  hideCloseBtn = false,
  closeTestId,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  closeIconStyle?: string;
  // Suppress the built-in top-right close affordance (e.g. the TPA Learning
  // Center consent modal, which forces an explicit Yes/No choice — SFCC-11508).
  hideCloseBtn?: boolean;
  // `data-testid` for the built-in close affordance, so a dialog whose X is a
  // documented QA hook can name it (ET-246). Omitted elsewhere — the button is
  // still reachable by its `sr-only` "Close" accessible name.
  closeTestId?: string;
}) => (
  <DialogPortal>
    <DialogOverlay className="fixed inset-0 bg-overlay backdrop-blur-sm" />
    <DialogPrimitive.Content
      ref={props.ref}
      // Vertical (top) slide only — no horizontal slide. In Tailwind v4 the
      // slide utilities animate `transform`, which compounds with the
      // `translate`-based centering rather than replacing it, so a left-slide
      // launches the modal from the top-left corner. Dropping it makes the
      // modal slide straight down from top-center.
      className={cn(
        // `data-[state=closed]:!pointer-events-none` — same stall guard as the
        // overlay above: Content is a Radix DismissableLayer that fades out in
        // place, so a stalled exit leaves it invisibly covering its footprint
        // (full-screen for e.g. the image-zoom usage). The `!` beats the
        // inline `pointer-events` Radix puts on it while the body lock is
        // active. Pinned by overlay-click-through.test.tsx.
        "fixed left-[50%] top-[50%] z-50 grid max-h-full w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-auto border bg-background p-6 shadow-lg duration-200 data-[state=closed]:!pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-full",
        className,
      )}
      {...props}
    >
      {children}
      {!hideCloseBtn && (
        <DialogPrimitive.Close
          data-testid={closeTestId}
          className={cn(
            "absolute right-4 top-3 flex h-7 w-7 flex-1-0-0 items-center justify-center self-stretch disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
            closeIconStyle,
          )}
        >
          {/* 20px, matching what the deleted `closeIconWidth`/`closeIconHeight`
              props resolved to — they defaulted to "20" and no call site ever
              overrode them, which is why removing them is safe. Kept as a
              STATIC class because the interpolated form they built,
              `h-[${n}px] w-[${n}px]`, was only ever generated by luck: Tailwind
              can't see a class assembled at runtime, so it existed solely
              because unrelated files (e.g. product-quick-nav.tsx) happen to
              contain the same literals. A caller wanting a different glyph size
              passes `closeIconStyle` with an `[&>svg]:` variant, which IS
              statically visible from its own file and wins on specificity. */}
          <DrawerCloseIcon className="h-[20px] w-[20px]" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    ref={props.ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    ref={props.ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
